import { sampleRandomColorFromPalette } from 'constants/colorPalette';
import {
  BUDGET_CALCULATION,
  BUDGET_INTERVAL,
  BUDGET_RECURRENCE_END,
  BUDGET_SCOPES,
  BUDGET_TYPES,
  RATE_TYPES,
} from 'constants/project';
import { currencyFactory } from 'factories/account';
import { DateTime } from 'luxon';
import type { TagsDictionary } from 'redux/modules/tags.types';
import type { Client } from 'types/client';
import type {
  BudgetRecurrence,
  Draft,
  EnrichedProject,
  EnrichedProjectWithBudget,
  ExchangeRate,
  Project,
  ProjectBase,
  ProjectLabel,
} from 'types/project';
import { TagSettings } from 'types/project';
import type { Merge } from 'types/utility';
import { keyBy } from './typesafe';

export function generateBudgetRecurrence(
  payload: Partial<BudgetRecurrence> = {}
): BudgetRecurrence {
  const defaults = {
    recur: BUDGET_INTERVAL.NONE,
    start_date: null,
    end_date: null,
    recur_until: BUDGET_RECURRENCE_END.ARCHIVED,
  };

  return {
    ...defaults,
    ...payload,
  };
}
interface GenerateProjectDraftOptions {
  recurringBudgetsAvailable?: boolean;
}

type GenerateProjectDraftInput = Partial<
  Merge<
    Project,
    {
      client: Draft['client'];
      users: Draft['users'];
    }
  >
>;

export function generateProjectDraft(
  payload?: GenerateProjectDraftInput,
  options: GenerateProjectDraftOptions = { recurringBudgetsAvailable: false }
): Draft {
  const defaults: Draft = {
    name: '',
    active: true,
    color: sampleRandomColorFromPalette(),
    client: {
      id: null,
      name: '',
      color: sampleRandomColorFromPalette(),
    },
    users: [],
    billable: true,
    budget: 0,
    team_ids: [],
    budget_type: BUDGET_TYPES.NONE,
    budget_scope: BUDGET_SCOPES.PROJECT,
    budget_progress: 0,
    budget_percent: 0,
    budget_calculation: BUDGET_CALCULATION.COMPLETED,
    invoice_by_budget: false,
    external_id: null,
    hour_rate: 0,
    rate_type: RATE_TYPES.NON_BILLABLE,
    labels: [],
    required_notes: false,
    required_labels: false,
    budget_recurrence: {
      recur: BUDGET_INTERVAL.NONE,
      start_date: null,
      end_date: null,
      recur_until: BUDGET_RECURRENCE_END.ARCHIVED,
    },
    label_ids: [],
    enable_labels: TagSettings.None,
    allow_only_one_tag: false,
    updated_at: DateTime.now().toISO(),
    update_hour_billable_state: null,
    update_hour_rates: null,
    update_hour_default_labels: null,
    currency: currencyFactory(),
    exchange_rate: [1, 1],
    created_from: 'Web',
  };

  const draft: Draft = { ...defaults, ...payload };

  if (!options.recurringBudgetsAvailable) {
    return draft;
  }

  return {
    ...draft,
    budget_recurrence: generateBudgetRecurrence(payload?.budget_recurrence),
  };
}

export function getAvailableProjectsForUser(
  projects: Array<EnrichedProject> = [],
  includeArchived: boolean = true,
  archivedClientIds: Array<number> = [],
  permissions: Array<number> = [],
  alwaysIncludeProjectIds: Array<number> = []
) {
  return projects.filter(project => {
    const hasPermission = permissions.includes(project.base.id);

    // If client is archived, the project should be considered archived as well
    const isClientArchived =
      project.base.client && archivedClientIds.includes(project.base.client.id);
    const passesArchivedFilter =
      includeArchived || (!includeArchived && project.base.active && !isClientArchived);
    const shouldAlwaysBeIncluded = alwaysIncludeProjectIds.includes(project.base.id);

    return shouldAlwaysBeIncluded || (hasPermission && passesArchivedFilter);
  });
}

export function getActiveProjects(
  projects: Array<EnrichedProject> = [],
  archivedClientIds: Array<number> = []
) {
  return projects.filter(project => {
    // If client is archived, the project should be considered archived as well
    const isClientArchived =
      project.base.client && archivedClientIds.includes(project.base.client.id);
    return project.base.active && !isClientArchived;
  });
}

export function removeIllegalLabelIds(
  project: Project,
  labelIdsOnDraft: Array<number>
): Array<number> {
  if (!project) {
    return labelIdsOnDraft;
  }
  // A project on "all tags" restricts nothing, so there is no illegal id to remove. Filtering it
  // anyway is not merely redundant, it is wrong: under TagSettings.All the backend fills label_ids
  // with every account tag id *as of when the project payload was built*, so a tag created after
  // that snapshot looks illegal and gets silently stripped. That is how a freshly synced
  // integration tag would vanish from an entry until the project happened to be re-fetched
  // (TT-5623).
  if (project.enable_labels === TagSettings.All) {
    return labelIdsOnDraft;
  }
  return labelIdsOnDraft.filter(id => project.label_ids.includes(id));
}

export interface ClientWithProjects<TProject = EnrichedProject> {
  client: Client;
  projects: Array<TProject>;
}
export function groupProjectsByClient(
  projects: Array<EnrichedProject>,
  clients: Array<Client>
): Record<number, ClientWithProjects> {
  const clientsById = keyBy(clients, 'id');
  return projects.reduce((data: Record<number, ClientWithProjects>, project) => {
    const clientId = project.base.client.id;
    const client = clientsById[clientId];

    if (client) {
      if (!data[clientId]) {
        data[clientId] = {
          client,
          projects: [project],
        };

        return data;
      }

      data[clientId].projects.push(project);
    }

    return data;
  }, {});
}

export const calculateBudgetProgress = (project: Project) => {
  if (!project.budget) {
    return 0;
  }
  if (project.budget_type === BUDGET_TYPES.HOUR && !!project.duration) {
    return project.duration.total_minutes;
  }
  if (project.budget_type === BUDGET_TYPES.MONEY && !!project.cost) {
    return project.cost.amount;
  }
  return 0;
};

export const calculateBudgetRemaining = (project: Project) => {
  if (!project.budget) {
    return 0;
  }
  if (project.budget_type === BUDGET_TYPES.HOUR && !!project.duration) {
    return project.budget - project.duration.total_hours;
  }

  if (project.budget_type === BUDGET_TYPES.MONEY && !!project.cost) {
    return project.budget - project.cost.amount;
  }

  return 0;
};

export const calculateBudgetPercent = (project: Project) => {
  if (!project.budget) {
    return 0;
  }
  if (project.budget_type === BUDGET_TYPES.HOUR && !!project.duration) {
    const totalHours = project.duration.total_minutes / 60;
    return (totalHours / project.budget) * 100;
  }

  if (project.budget_type === BUDGET_TYPES.MONEY && !!project.cost) {
    return (project.cost.amount / project.budget) * 100;
  }

  return 0;
};

export const calculateExchangeRate = (exchangeRate?: ExchangeRate): number => {
  if (!exchangeRate) return 1;

  return (exchangeRate[1] || 1) / (exchangeRate[0] || 1) || 1;
};

export const mapDefaultTags = (draftTags: Array<ProjectLabel>, tags: TagsDictionary) => {
  const map: Record<number, number> = {};
  const defaultTags = draftTags.filter(tag => tag.default);

  // Loop through the default tags and map children to their parents
  for (let i = 0; i < defaultTags.length; i++) {
    const tag = defaultTags[i];

    const sourceTag = tags[tag.label_id];
    if (sourceTag.parent_id) {
      map[sourceTag.parent_id] = sourceTag.id;
    } else {
      map[sourceTag.id] = sourceTag.id;
    }
  }

  return map;
};

/**
 * Since only the selected child is saved on the project for default tags, we need to backfill
 * sibling tags while ensuring they appear in the correct order.
 */
const injectDefaultTagChildren = (
  draftTags: Array<ProjectLabel>,
  defaultTags: Record<number, number>,
  tags: TagsDictionary
) => {
  const defaultParentTagIds = Object.keys(defaultTags).map(Number);

  // Keep track of added IDs so we don't get duplicates
  const uniqueIds = new Set<number>();

  return draftTags.reduce<Array<ProjectLabel>>((mappedTags, tag) => {
    if (uniqueIds.has(tag.label_id)) {
      return mappedTags;
    }

    uniqueIds.add(tag.label_id);
    mappedTags.push(tag);

    if (defaultParentTagIds.includes(tag.label_id)) {
      const childIds = tags[tag.label_id].children ?? [];
      const children = childIds
        .filter(id => !uniqueIds.has(id))
        .map(id => {
          uniqueIds.add(id);
          return { label_id: id, budget: 0, default: true } as ProjectLabel;
        });

      mappedTags.push(...children);
    }

    return mappedTags;
  }, []);
};

export const mapDefaultTagsForDraft = (draft: Draft, tags: TagsDictionary) => {
  if (!draft.labels.length) return;

  const defaultTags = mapDefaultTags(draft.labels, tags);

  draft.defaultTags = defaultTags;
  draft.labels = injectDefaultTagChildren(draft.labels, defaultTags, tags);
};
export const isEnrichedProjectWithBudget = (
  project?: EnrichedProject | null
): project is EnrichedProjectWithBudget =>
  !!(project as EnrichedProjectWithBudget)?.budgetAttributes;

export const getProjectColor = (project: ProjectBase | undefined | null): string => {
  if (!project) return sampleRandomColorFromPalette();
  if (project.color) return project.color;
  if (project.client) return project.client.color;
  return sampleRandomColorFromPalette();
};
